import type { ReactNode } from 'react'
import { Link } from 'react-router'
import { DisplayHeading } from '@carouselgg/nexus/display-heading'
import styles from './DiscoveryHero.module.css'

interface DiscoveryHeroProps {
  eyebrow: string
  /**
   * The headline. Use `<br />` to control the line break — the display cut is
   * set tight enough that letting it wrap on its own reads badly.
   */
  title: ReactNode
  subtext: string
  /** Figures or facts set opposite the headline. Drops below it on narrow screens. */
  aside?: ReactNode
}

/**
 * Shared hero for the discovery pages (`/compete`, `/orgs`, `/news`, `/for/*`).
 *
 * Left-aligned and greyscale on purpose. These pages sit in front of a list, so
 * the hero states where you are and gets out of the way; a centred gradient
 * slab pushed the first actual result below the fold.
 */
export function DiscoveryHero({ eyebrow, title, subtext, aside }: DiscoveryHeroProps) {
  return (
    <header className={styles.hero}>
      <div className={styles.copy}>
        <p className={styles.eyebrow}>{eyebrow}</p>
        <DisplayHeading as="h1" size="7xl" uppercase={false} className={styles.title}>
          {title}
        </DisplayHeading>
        <p className={styles.subtext}>{subtext}</p>
      </div>
      {aside && <div className={styles.aside}>{aside}</div>}
    </header>
  )
}

interface HeroFigureProps {
  value: string
  label: string
}

/** One large figure for the hero's `aside`. */
export function HeroFigure({ value, label }: HeroFigureProps) {
  return (
    <div className={styles.figure}>
      <span className={styles.figureValue}>{value}</span>
      <span className={styles.figureLabel}>{label}</span>
    </div>
  )
}

interface DiscoveryCTAProps {
  title: string
  subtext: string
  to: string
  label: string
  /** Optional second, lower-emphasis action. */
  secondaryTo?: string
  secondaryLabel?: string
}

/** Shared closing CTA for the same set of pages. */
export function DiscoveryCTA({
  title,
  subtext,
  to,
  label,
  secondaryTo,
  secondaryLabel,
}: DiscoveryCTAProps) {
  return (
    <section className={styles.cta}>
      <div className={styles.ctaCopy}>
        <DisplayHeading as="h2" size="5xl" uppercase={false} className={styles.ctaTitle}>
          {title}
        </DisplayHeading>
        <p className={styles.ctaSubtext}>{subtext}</p>
      </div>
      <div className={styles.ctaActions}>
        <Link to={to} className={styles.ctaButton}>{label}</Link>
        {secondaryTo && secondaryLabel && (
          <Link to={secondaryTo} className={styles.ctaSecondary}>{secondaryLabel}</Link>
        )}
      </div>
    </section>
  )
}
